/**
 * Hooks de la capa "Estudio Contable" (Portal de cartera) — TanStack Query.
 *
 * Consumen los endpoints `/firm/*`. Estos endpoints se sirven desde el subdominio
 * del tenant ACTIVO pero operan sobre el schema público (modelos del estudio) y,
 * en el caso de la cartera, agregan datos cross-tenant vía `schema_context`. Por
 * eso el portal funciona desde cualquier empresa donde esté el usuario.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'

// ─── Tipos (espejo de las respuestas del backend apps/firms) ──────────────────

export type FirmRole = 'firm_admin' | 'firm_staff'
export type SemaforoEstado = 'pendiente' | 'pagado' | 'presentado' | 'vencido' | 'sin_pago'
export type PayrollStatus =
  | 'no_existe' | 'draft' | 'calculated' | 'approved' | 'paid' | 'closed' | 'error'

export interface UserMini {
  id: string
  email: string
  full_name: string
}

export interface AccountingFirm {
  id: string
  name: string
  tax_id: string
  plan: string
  billing_email: string
  status: 'active' | 'suspended'
  billing_policy: 'per_client' | 'flat' | 'tiered'
  bill_own_tenant: boolean
  created_at: string
  updated_at: string
}

export interface FirmContext {
  firm: AccountingFirm
  firm_role: FirmRole
  clients_count: number
}

export interface FirmClient {
  id: string
  firm: string
  tenant_schema: string
  tenant_name: string
  onboarding_status: 'pending' | 'active'
  assigned_to: string | null
  assigned_to_detail: UserMini | null
  is_active: boolean
  is_own_company: boolean
  billable: boolean
  created_at: string
}

/** Semáforo tributario de 3 capas (F29 mensual / F22 anual). */
export interface Semaforo {
  kind: 'F29' | 'F22'
  period: string
  monto: string
  resultado: string
  due_date: string
  paid: boolean | null
  paid_source: string | null
  filed: boolean
  filed_at: string | null
  estado: SemaforoEstado
}

/** Snapshot operativo de un cliente. Cualquier métrica puede faltar (`_safe`). */
export interface Snapshot {
  computed_at?: string
  f29?: Semaforo | null
  f22?: Semaforo | null
  payroll?: { period: string; status: PayrollStatus } | null
  dtes_por_aceptar?: number | null
  vencidos?: { cxc: number; cxp: number } | null
  cierre?: { current_date_locked: boolean } | null
}

export interface PortfolioClient {
  tenant_schema: string
  tenant_name: string
  assigned_to: UserMini | null
  snapshot: Snapshot
}

export interface PortfolioSummary {
  clients: number
  f29_pending: number
  f22_pending: number
  dtes_por_aceptar: number
  payroll_pending: number
}

export interface PortfolioResponse {
  by_client: PortfolioClient[]
  summary: PortfolioSummary
  count: number
}

export interface FirmStaffMember {
  id: string
  firm: string
  user: string
  user_detail: UserMini
  firm_role: FirmRole
  status: 'pending' | 'accepted' | 'revoked'
  invited_by: string | null
  created_at: string
}

export interface FirmClientRequest {
  id: string
  firm: string
  kind: 'new' | 'link'
  company_name: string
  tax_id: string
  existing_tenant_schema: string
  notes: string
  status: 'pending' | 'approved' | 'rejected'
  requested_by: string | null
  requested_by_detail: UserMini | null
  resolved_by: string | null
  resolved_at: string | null
  resolved_tenant_schema: string
  created_at: string
}

export interface ProvisionResult {
  provisioned: string[]
  revoked: string[]
  skipped: string[]
}

export interface TaxFiling {
  id: string
  kind: 'F29' | 'F22'
  period: string
  status: 'filed' | 'pending'
  filed_at: string | null
  folio: string
  updated_at: string
}

// ─── Keys de cache ────────────────────────────────────────────────────────────
export const firmKeys = {
  context: ['firm', 'context'] as const,
  clients: ['firm', 'clients'] as const,
  portfolio: ['firm', 'portfolio'] as const,
  staff: ['firm', 'staff'] as const,
  requests: ['firm', 'requests'] as const,
}

// ─── Queries ──────────────────────────────────────────────────────────────────

export function useFirmContext() {
  return useQuery({
    queryKey: firmKeys.context,
    queryFn: async (): Promise<FirmContext> => {
      const { data } = await apiClient.get<FirmContext>('/firm/')
      return data
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useFirmClients() {
  return useQuery({
    queryKey: firmKeys.clients,
    queryFn: async (): Promise<FirmClient[]> => {
      const { data } = await apiClient.get<{ clients: FirmClient[]; count: number }>('/firm/clients/')
      return data.clients
    },
  })
}

export function useFirmPortfolio() {
  return useQuery({
    queryKey: firmKeys.portfolio,
    queryFn: async (): Promise<PortfolioResponse> => {
      const { data } = await apiClient.get<PortfolioResponse>('/firm/portfolio/')
      return data
    },
    // El backend cachea el snapshot por tenant (TTL 25h); en el cliente lo
    // mantenemos fresco unos minutos para no re-pegar en cada navegación.
    staleTime: 2 * 60 * 1000,
  })
}

export function useFirmStaff() {
  return useQuery({
    queryKey: firmKeys.staff,
    queryFn: async (): Promise<FirmStaffMember[]> => {
      const { data } = await apiClient.get<{ staff: FirmStaffMember[]; count: number }>('/firm/staff/')
      return data.staff
    },
  })
}

export function useFirmRequests() {
  return useQuery({
    queryKey: firmKeys.requests,
    queryFn: async (): Promise<FirmClientRequest[]> => {
      const { data } = await apiClient.get<{ requests: FirmClientRequest[]; count: number }>(
        '/firm/clients/requests/',
      )
      return data.requests
    },
  })
}

// ─── Mutations ──────────────────────────────────────────────────────────────────

export function useUpdateFirmClient() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: {
      id: string
      payload: { assigned_to?: string | null; is_active?: boolean }
    }): Promise<FirmClient> => {
      const { data } = await apiClient.patch<FirmClient>(`/firm/clients/${id}/`, payload)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: firmKeys.clients })
      qc.invalidateQueries({ queryKey: firmKeys.portfolio })
    },
  })
}

export function useProvisionStaff() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: {
      user_id: string
      role: string
      tenant_schemas: string[]
      revoke?: boolean
    }): Promise<ProvisionResult> => {
      const { data } = await apiClient.post<ProvisionResult>('/firm/provision/', payload)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: firmKeys.clients })
    },
  })
}

export interface FirmStaffCandidates {
  /** Tenant propio del estudio (is_own_company); null si no se autogestiona. */
  own_tenant: { tenant_schema: string; tenant_name: string } | null
  /** Usuarios del tenant propio que aún no son miembros del estudio. */
  users: UserMini[]
}

export function useFirmStaffCandidates() {
  return useQuery({
    queryKey: ['firm', 'staff-candidates'] as const,
    queryFn: async (): Promise<FirmStaffCandidates> => {
      const { data } = await apiClient.get<FirmStaffCandidates>('/firm/staff/candidates/')
      return data
    },
  })
}

export function useAddStaff() {
  const qc = useQueryClient()
  return useMutation({
    // user_id (selector de tu empresa) o email (enganchar un usuario EXISTENTE).
    mutationFn: async (payload: { user_id?: string; email?: string; firm_role: FirmRole }): Promise<FirmStaffMember> => {
      const { data } = await apiClient.post<FirmStaffMember>('/firm/staff/', payload)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: firmKeys.staff })
      qc.invalidateQueries({ queryKey: ['firm', 'staff-candidates'] })
    },
  })
}

export function useCreateClientRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: {
      kind: 'new' | 'link'
      company_name: string
      tax_id?: string
      existing_tenant_schema?: string
      notes?: string
    }): Promise<FirmClientRequest> => {
      const { data } = await apiClient.post<FirmClientRequest>('/firm/clients/requests/', payload)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: firmKeys.requests })
    },
  })
}

export function useMarkFiling() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: {
      tenant_schema: string
      kind: 'F29' | 'F22'
      period: string
      status?: 'filed' | 'pending'
      filed_at?: string
      folio?: string
    }): Promise<{ filing: TaxFiling; snapshot: Snapshot }> => {
      const { data } = await apiClient.post<{ filing: TaxFiling; snapshot: Snapshot }>(
        '/firm/clients/tax-filing/', payload,
      )
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: firmKeys.portfolio })
    },
  })
}
